import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import Text from 'components/_UI/Text';
import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import { useForm } from 'react-hook-form';
import fieldValidations from 'helpers/fieldValidations';
import fieldMessages from 'texts/messages/field';
import userApi from 'api/requests/user';
import { AlertCircle, ArrowRight, ArrowLeft, User, Mail, Lock } from 'react-feather';

const errorMessages = {
  unique: 'E-mail já cadastrado',
  fail: 'Ocorreu um erro, tente novamente.',
};

export default function SignUpForm({ onNext, onBack, initialData }) {
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      firstName: initialData?.first_name || '',
      lastName: initialData?.last_name || '',
      email: initialData?.email || '',
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password');

  async function onSubmit({ firstName, lastName, email, password, confirmPassword }) {
    setEmailError('');

    if (password !== confirmPassword) {
      return;
    }

    setIsLoading(true);

    try {
      const { data } = await userApi.dataEnable({ email });

      if (!data.email) {
        setEmailError(errorMessages.unique);
        return;
      }

      onNext({
        first_name: firstName,
        last_name: lastName,
        email,
        password,
      });
    } catch (error) {
      setEmailError(errorMessages.fail);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Container>
      <HeaderArea>
        <BackButton onClick={onBack} aria-label="Voltar para login">
          <ArrowLeft size={18} color={colors.night} />
        </BackButton>
        <StepIndicator>
          <StepDot active />
          <StepDot />
        </StepIndicator>
      </HeaderArea>

      <IconCircle>
        <User size={22} color={colors.occasionalPurple} />
      </IconCircle>
      <Title>Crie sua conta</Title>
      <Subtitle>Preencha seus dados para começar</Subtitle>

      {emailError && (
        <ErrorBanner>
          <AlertCircle size={16} color={colors.danger} />
          <Text color={colors.danger} name="small" type="bold">
            {emailError}
          </Text>
        </ErrorBanner>
      )}

      <FormWrapper onSubmit={handleSubmit(onSubmit)}>
        <FieldRow>
          <FieldGroup>
            <FieldLabel>
              Nome<Required>*</Required>
            </FieldLabel>
            <Fieldset
              layout="squired"
              placeholder="Seu nome"
              name="firstName"
              register={register}
              validations={fieldValidations.name()}
            />
            {errors.firstName?.message && (
              <ErrorText>{errors.firstName.message}</ErrorText>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>
              Sobrenome<Required>*</Required>
            </FieldLabel>
            <Fieldset
              layout="squired"
              placeholder="Seu sobrenome"
              name="lastName"
              register={register}
              validations={fieldValidations.name('sobrenome')}
            />
            {errors.lastName?.message && (
              <ErrorText>{errors.lastName.message}</ErrorText>
            )}
          </FieldGroup>
        </FieldRow>

        <FieldGroup>
          <FieldLabel>
            E-mail<Required>*</Required>
          </FieldLabel>
          <Fieldset
            layout="squired"
            placeholder="seu@email.com"
            name="email"
            register={register}
            validations={fieldValidations.email()}
          />
          {errors.email?.message && (
            <ErrorText>{errors.email.message}</ErrorText>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>
            Senha<Required>*</Required>
          </FieldLabel>
          <Fieldset
            layout="squired"
            placeholder="Crie uma senha"
            inputType="password"
            badgeIconPassword
            badgeIconColorPassword={colors.night}
            badgeIconPositionPassword="right"
            name="password"
            register={register}
            validations={fieldValidations.password('senha')}
          />
          {errors.password?.message && (
            <ErrorText>{errors.password.message}</ErrorText>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>
            Confirmar senha<Required>*</Required>
          </FieldLabel>
          <Fieldset
            layout="squired"
            placeholder="Repita sua senha"
            inputType="password"
            badgeIconPassword
            badgeIconColorPassword={colors.night}
            badgeIconPositionPassword="right"
            name="confirmPassword"
            register={register}
            validations={{
              ...fieldValidations.password('confirmação de senha'),
              validate: (value) =>
                value === password || fieldMessages.passwordMustBeEqual,
            }}
          />
          {errors.confirmPassword?.message && (
            <ErrorText>{errors.confirmPassword.message}</ErrorText>
          )}
        </FieldGroup>

        {isLoading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <SubmitButton type="submit">
            <span>Continuar</span>
            <ArrowRight size={18} />
          </SubmitButton>
        )}
      </FormWrapper>
    </Container>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to { opacity: 1; transform: translateX(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeIn} 0.25s ease-out;
`;

const HeaderArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.neutralDark};
  }
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const StepDot = styled.div`
  width: ${({ active }) => (active ? '24px' : '8px')};
  height: 8px;
  border-radius: 4px;
  background: ${({ active }) => (active ? colors.occasionalPurple : colors.neutralDark)};
  transition: all 0.3s ease;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f3eaff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.nightDark};
  margin: 0;
  text-align: center;

  @media screen and (max-width: ${breakpoints.sm}) {
    font-size: 18px;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${colors.night};
  margin-top: 4px;
  margin-bottom: 20px;
  text-align: center;
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;

  @media screen and (max-width: ${breakpoints.sm}) {
    flex-direction: column;
  }
`;

const FieldGroup = styled.div`
  width: 100%;
  flex: 1;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: ${colors.nightDark};
  letter-spacing: 0.01em;
  margin-bottom: 6px;
`;

const Required = styled.span`
  color: ${colors.danger};
`;

const ErrorText = styled.p`
  color: ${colors.danger};
  font-size: 12px;
  margin-top: 4px;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: #fef2f2;
  border: 1px solid ${colors.dangerLight};
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 280px;
  margin: 8px auto 0;
  padding: 12px 28px;
  background: ${colors.secondaryPurple};
  color: ${colors.neutralLight};
  border: 1px solid ${colors.secondaryPurple};
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 3px ${colors.nightLight};

  &:hover {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(128, 37, 232, 0.25);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 0;
`;
